import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {
    SubscriptionRow,
} from 'src/modules/profile/account/tabs/account-profile/product-access-summary-section/subscription-row';
import {useAccountProductAccessTerms} from 'src/modules/profile/user/hooks/use-account-product-access-terms';
import {useAssignSeatToUser} from 'src/modules/profile/user/hooks/use-assign-seat-to-user';
import {useUserSeatAssignments} from 'src/modules/profile/user/hooks/use-user-seat-assignments';
import {ProductSubscriptionLifecycleStatus} from 'src/openapi/admin/models/ProductSubscriptionLifecycleStatus';
import {getError} from 'src/utils/get-error';

import {BoxLoading} from '@pb/kit-component-box-loading';
import Button, {FlatButton} from '@pb/kit-component-button';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group, Item} from '@pb/kit-component-group';
import {Message} from '@pb/kit-component-message';
import {PaginationStripe} from '@pb/kit-component-pagination';

const PAGE_SIZE = 5;

type Props = {
    accountId: number,
    userId: number,
    onBusyChange: (busy: boolean) => void,
    onClose: () => void,
    onAssigned: () => void,
};

const getStatusOrder = (status: ProductSubscriptionLifecycleStatus) => (
    status === ProductSubscriptionLifecycleStatus.ACTIVE ? 0 : 1
);

export const AssignSeatModal: React.FC<Props> = ({accountId, userId, onBusyChange, onClose, onAssigned}) => {
    const {terms, error: termsError} = useAccountProductAccessTerms(accountId);
    const {assignments, error: assignmentsError} = useUserSeatAssignments(userId, undefined, 1, 250);
    const {assign, assigning, error: assignError} = useAssignSeatToUser(userId);
    const [removedTermIds, setRemovedTermIds] = useState<string[]>([]);
    const [page, setPage] = useState(1);

    const availableTerms = useMemo(() => {
        const assignedTermIds = new Set(assignments?.map(assignment => assignment.productSubscriptionId));
        const removedTerms = new Set(removedTermIds);

        return (terms || [])
            .filter(term =>
                !assignedTermIds.has(term.productSubscriptionId) && !removedTerms.has(term.productSubscriptionId))
            .sort((left, right) => {
                const statusOrder = getStatusOrder(left.lifecycleStatus) - getStatusOrder(right.lifecycleStatus);

                return statusOrder || left.product.name.localeCompare(right.product.name);
            });
    }, [assignments, removedTermIds, terms]);
    const pageCount = Math.max(1, Math.ceil(availableTerms.length / PAGE_SIZE));
    const currentPage = Math.min(page, pageCount);
    const visibleTerms = availableTerms.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const loading = terms === null || assignments === null;
    const loadError = termsError || assignmentsError;

    useEffect(() => {
        onBusyChange(assigning);
    }, [assigning, onBusyChange]);

    const onAssign = useCallback(async (productSubscriptionId: string) => {
        if (await assign(productSubscriptionId)) {
            setRemovedTermIds(ids => [...ids, productSubscriptionId]);
            onAssigned();
        }
    }, [assign, onAssigned]);

    return (
        <>
            <DialogHeader onClose={onClose}>Assign Product Seat</DialogHeader>
            <DialogMain style={{padding: 24}}>
                <Group direction="v" gap="m">
                    {loadError ?
                            <Message color="error">{getError({error: loadError, separator: '. '})}</Message> : null}
                    {assignError ? <Message color="error">{assignError}</Message> : null}
                    <BoxLoading loading={loading}>
                        <Group direction="v" gap="m">
                            {visibleTerms.map(term => (
                                <Group key={term.productSubscriptionId} justify="space-between" alignItems="center">
                                    <Item grow={1}><SubscriptionRow subscription={term}/></Item>
                                    <FlatButton
                                        disabled={assigning || term.activeSeatCount >= term.seats}
                                        onClick={() => onAssign(term.productSubscriptionId)}>
                                        Assign
                                    </FlatButton>
                                </Group>
                            ))}
                            {!loading && !loadError && availableTerms.length === 0 ?
                                    <span>No Product Access Terms are available</span> : null}
                        </Group>
                        <PaginationStripe
                            goToPage={false}
                            pageSize={PAGE_SIZE}
                            totalCount={availableTerms.length}
                            currentPage={currentPage}
                            onPaginationChange={pagination => setPage(pagination.currentPage)}
                            sizeSwitcherPosition="LEFT"
                            sizeSwitcherType="NONE"
                        />
                    </BoxLoading>
                </Group>
            </DialogMain>
            <DialogFooter>
                <Button variant="Type.SECONDARY" disabled={assigning} onClick={onClose}>Close</Button>
            </DialogFooter>
        </>
    );
};
